import {generateRandomKey} from "../utils/functions";
import {ContentCellMatrix, RoleCellMatrix} from "./CellMatrix";
import {Funnel, FunnelHeaders} from "./Funnel";
import {ContentHeader, ContentHeaders} from "./ContentHeader";
import {RoleHeader, RoleHeaders} from "./RoleHeader";
import {CellContent} from "./CellContent";
import {ContentKey, ContentKeyPair, FunnelKey, RoleKey} from "./Keys";
import {RoleCellData} from "./RoleCell";

const INITIAL_FUNNEL_KEY = '10000001' as FunnelKey;
const INITIAL_CONTENT_KEY = '10000002' as ContentKey;

export class CjmapCells {
    private readonly funnels: FunnelHeaders;
    private readonly content_headers: ContentHeaders;
    private readonly role_headers: RoleHeaders;
    private readonly content_cell_matrix: ContentCellMatrix;
    private readonly role_cell_matrix: RoleCellMatrix;

    constructor(funnels: Funnel[], content_headers: ContentHeader[], role_headers: RoleHeader[],
                content_cells: CellContent[], role_cells: RoleCellData[]) {

        if (content_cells.length === 0) {
            throw new Error('no content_cells');
        }

        this.funnels = new FunnelHeaders(funnels);
        this.content_headers = new ContentHeaders(content_headers);
        this.role_headers = new RoleHeaders(role_headers);
        this.content_cell_matrix = new ContentCellMatrix(content_cells, this.funnels.Keys, this.content_headers.Keys);
        this.role_cell_matrix = new RoleCellMatrix(role_cells, this.funnels.Keys, this.role_headers.Keys);
    }

    private clone(
        replacement: {
            new_funnels?: Funnel[];
            new_content_headers?: ContentHeader[];
            new_role_headers?: RoleHeader[];
            new_content_cells?: CellContent[];
            new_role_cells?: RoleCellData[];
        }) {

        return new CjmapCells(
            replacement.new_funnels ? replacement.new_funnels : this.funnels.clone(),
            replacement.new_content_headers ? replacement.new_content_headers : this.content_headers.clone(),
            replacement.new_role_headers ? replacement.new_role_headers : this.role_headers.clone(),

            replacement.new_content_cells ? replacement.new_content_cells : this.content_cell_matrix.clone(),
            replacement.new_role_cells ? replacement.new_role_cells : this.role_cell_matrix.clone()
        );
    }

    get FunnelHeaders(): FunnelHeaders {
        return this.funnels;
    }

    get ContentHeaders(): ContentHeaders {
        return this.content_headers;
    }

    get RoleHeaders(): RoleHeaders {
        return this.role_headers;
    }

    get ContentCellMatrix(): ContentCellMatrix {
        return this.content_cell_matrix;
    }

    get RoleCellMatrix(): RoleCellMatrix {
        return this.role_cell_matrix;
    }

    createFunnelKey(): FunnelKey {
        // TODO: key 競合時の処理
        return generateRandomKey() as FunnelKey;
    }

    createRoleKey() {
        // TODO: key 競合時の処理
        return generateRandomKey() as RoleKey;
    }

    createContentKey(): ContentKey {
        // TODO: key 競合時の処理
        return generateRandomKey() as ContentKey;
    }

    appendFunnel(funnel: Funnel, position: number) {
        if (this.FunnelHeaders.hasKey(funnel.Key)) {
            throw new Error("funnel key already exists: " + funnel.Key);
        }
        if (position < 0 || this.FunnelHeaders.Length < position) {
            throw new Error("funnel position out of bounce: " + position);
        }

        const new_funnels = this.FunnelHeaders.append(funnel, position);
        const new_role_cells = this.role_cell_matrix.appendColumn(position);
        const new_content_cells = this.content_cell_matrix.appendColumn(position);

        return this.clone({new_funnels, new_content_cells, new_role_cells});
    }

    deleteFunnel(funnel_key: FunnelKey) {
        const index = this.FunnelHeaders.findIndexByKey(funnel_key);

        const new_funnels = this.FunnelHeaders.delete(index);
        const new_content_cells = this.content_cell_matrix.deleteColumn(index);
        const new_role_cells = this.role_cell_matrix.deleteColumn(index);

        return this.clone({new_funnels, new_content_cells, new_role_cells})
    }

    moveFunnel(funnel_key: FunnelKey, index: number) {
        const from_index = this.FunnelHeaders.findIndexByKey(funnel_key);
        const to_index = index;

        const new_funnels = this.FunnelHeaders.move(from_index, to_index);
        const new_content_cells = this.content_cell_matrix.moveColumn(from_index, to_index);
        const new_role_cells = this.role_cell_matrix.moveColumn(from_index, to_index);

        return this.clone({new_funnels, new_content_cells, new_role_cells});
    }

    resizeFunnel(funnel_key: FunnelKey, width: number) {
        const new_funnels = this.FunnelHeaders.cloneWithReplacement(
            funnel => funnel.Key === funnel_key ? funnel.withSize(width) : null
        );

        return this.clone({new_funnels});
    }

    renameFunnel(funnel_key: FunnelKey, new_name: string) {
        const new_funnels = this.FunnelHeaders.cloneWithReplacement(
            funnel => funnel.Key === funnel_key ? funnel.withName(new_name) : null
        );

        return this.clone({new_funnels});
    }

    changeFunnelGroup(funnel_keys: FunnelKey[], new_name: string) {
        const new_funnels = this.FunnelHeaders.cloneWithReplacement(
            funnel => funnel_keys.some(key => key === funnel.Key) ? funnel.withGroup(new_name) : null
        );

        return this.clone({new_funnels});
    }

    showFunnels(funnel_keys: FunnelKey[]) {
        const new_funnels = this.FunnelHeaders.cloneWithReplacement(
            funnel => funnel_keys.some(key => key === funnel.Key) ? funnel.withIsHidden(false) : null
        );

        return this.clone({new_funnels});
    }

    hideFunnels(funnel_keys: FunnelKey[]) {
        const new_funnels = this.FunnelHeaders.cloneWithReplacement(
            funnel => funnel_keys.some(key => key === funnel.Key) ? funnel.withIsHidden(true) : null
        );

        return this.clone({new_funnels});
    }

    showAllFunnelAndContentLine() {
        const new_funnels = this.FunnelHeaders.cloneWithReplacement(
            f => f.IsHidden ? f.withIsHidden(false) : null
        );
        const new_content_headers = this.ContentHeaders.cloneWithReplacement(
            c => c.IsHidden ? c.withIsHidden(false) : null
        );
        return this.clone({new_funnels, new_content_headers});
    }

    appendContentHeader(content_header: ContentHeader, position: number) {
        if (this.ContentHeaders.hasKey(content_header.Key)) {
            throw new Error("content key already exists: " + content_header.Key);
        }
        if (position < 0 || this.ContentHeaders.Length < position) {
            throw new Error("content_header position out of bounce: " + position);
        }

        const new_content_headers = this.ContentHeaders.append(content_header, position);
        const new_content_cells = this.content_cell_matrix.appendRow(position);

        return this.clone({new_content_headers, new_content_cells});
    }

    renameContentHeader(content_key: ContentKey, new_name: string) {
        const new_content_headers = this.ContentHeaders.cloneWithReplacement(
            c => c.Key === content_key ? c.withName(new_name) : null
        );

        return this.clone({new_content_headers});
    }

    moveContentLine(content_key: ContentKey, index: number) {
        const from_index = this.ContentHeaders.findIndexByKey(content_key);
        const to_index = index;

        const new_content_headers = this.ContentHeaders.move(from_index, to_index);
        const new_content_cells = this.content_cell_matrix.moveRow(from_index, to_index)

        return this.clone({new_content_headers, new_content_cells});
    }

    deleteContentLine(key: ContentKey) {
        const index = this.ContentHeaders.findIndexByKey(key);

        const new_content_headers = this.ContentHeaders.delete(index);
        const new_content_cells = this.content_cell_matrix.deleteRow(index);

        return this.clone({new_content_headers, new_content_cells});
    }

    resizeContentLine(content_key: ContentKey, height: number) {
        const new_content_headers = this.ContentHeaders.cloneWithReplacement(
            c => c.Key === content_key ? c.withSize(height) : null
        );

        return this.clone({new_content_headers});
    }

    showContentLines(content_keys: ContentKey[]) {
        const new_content_headers = this.ContentHeaders.cloneWithReplacement(
            c => content_keys.some(k => k === c.Key) ? c.withIsHidden(false) : null
        );

        return this.clone({new_content_headers});
    }

    hideContentLines(content_keys: ContentKey[]) {
        const new_content_headers = this.ContentHeaders.cloneWithReplacement(
            c => content_keys.some(k => k === c.Key) ? c.withIsHidden(true) : null
        );

        return this.clone({new_content_headers});
    }


    appendRoleHeader(role_header: RoleHeader, position: number) {
        const new_role_headers = this.RoleHeaders.append(role_header, position);
        const new_role_cells = this.role_cell_matrix.appendRow(position);

        return this.clone({new_role_headers, new_role_cells});
    }

    renameRoleHeader(role_key: RoleKey, new_name: string) {
        const new_role_headers = this.RoleHeaders.cloneWithReplacement(
            r => r.Key === role_key ? r.withName(new_name) : null
        );

        return this.clone({new_role_headers});
    }

    deleteRoleLine(role_key: RoleKey) {
        const index = this.RoleHeaders.findIndexByKey(role_key);

        const new_role_headers = this.RoleHeaders.delete(index);
        const new_role_cells = this.role_cell_matrix.deleteRow(index);

        return this.clone({new_role_headers, new_role_cells});
    }

    changeRoleCellName(role_key: RoleKey, funnel_keys: FunnelKey[], new_name: string) {
        const keys = funnel_keys.map(funnel_key => 0 || {column_key: funnel_key, row_key: role_key});
        const new_role_cells = this.role_cell_matrix.updateAll(keys, new_name);
        return this.clone({new_role_cells});
    }

    moveRoleLine(role_key: RoleKey, position: number) {
        const from_index = this.RoleHeaders.findIndexByKey(role_key);
        const to_index = position;

        const new_role_headers = this.RoleHeaders.move(from_index, to_index);
        const new_role_cells = this.role_cell_matrix.moveRow(from_index, to_index);

        return this.clone({new_role_headers, new_role_cells});
    }

    updateCellContent(content_key_pair: ContentKeyPair, data: CellContent) {
        const new_content_cells = this.content_cell_matrix.update(content_key_pair.FunnelKey, content_key_pair.LineKey, data);
        return this.clone({new_content_cells});
    }


    static from(origin: CjmapCells) {
        return origin.clone({});
    }

    static createNew() {
        return new CjmapCells(
            [
                new Funnel(INITIAL_FUNNEL_KEY, "", 200, "", false)
            ],
            [
                ContentHeader.createDefault(INITIAL_CONTENT_KEY, 'perception')
            ],
            [],
            [CellContent.createNew()],
            []
        );
    }

    static fromJSON(obj: any) {

        if (!(obj.role_headers instanceof Array)) {
            throw new Error("invalid role_headers");
        }

        // TODO: 内容の確認
        return new CjmapCells(
            obj.funnels.map((funnel: any) => Funnel.fromJSON(funnel)),
            obj.content_headers.map((content_header: any) => ContentHeader.fromJSON(content_header)),
            obj.role_headers.map((role_header: any) => RoleHeader.fromJSON(role_header)),
            obj.content_cells.map((content_cell_data: any) => CellContent.fromJSON(content_cell_data)),
            Array.from(obj.role_cells),
        );
    }

    toJSON() {
        return {
            role_headers: this.role_headers.toJSON(),
            content_headers: this.content_headers.toJSON(),
            funnels: this.funnels.toJSON(),
            content_cells: this.content_cell_matrix.toJSON(),
            role_cells: this.role_cell_matrix.toJSON(),
        };
    }
}
